// ============================================================================
// USE TABLETOP FEED - Polls the second-screen aggregation endpoint for a live
// tabletop run and hands the snapshot back to the caller (LiveSession), which
// folds it into the state object passed to useSecondScreenBroadcast.
//
// Unlike useCinematicFeed (an incremental event feed with a localStorage
// cursor), this is a full-snapshot poll — tabletop-second-screen.service.ts's
// buildSecondScreenState already recomputes the whole picture server-side on
// every call, so there is no cursor to track here.
//
// Also resolves runId from sessionId once (a tabletop exercise doesn't carry
// its own routing param inside LiveSession — the same reverse-lookup problem
// TabletopRunTab.tsx solves on the run-console side), then polls the run's
// state every 5s, matching the backend clock's own tick interval.
//
// Only runs when the caller says Tabletop is usable here (the user holds the
// module and the project has it switched on): the backend answers 403
// otherwise. A 401/403/404 stops the poll for good, so a project that loses
// the module mid-session does not keep hammering the API.
// ============================================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { tabletopExercisesAPI, tabletopSecondScreenAPI } from '../services/tabletopService';
import type { TabletopSecondScreenState } from '../types/second-screen.types';

const POLL_INTERVAL_MS = 5_000;
const STOP_STATUSES = new Set([401, 403, 404]);

export function useTabletopFeed(
  projectId: string | undefined,
  sessionId: string,
  isActive: boolean,
  tabletopAvailable = false
): TabletopSecondScreenState | null {
  const [state, setState] = useState<TabletopSecondScreenState | null>(null);
  const runIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppedRef = useRef(false);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const poll = useCallback(async () => {
    if (!projectId || stoppedRef.current) return;
    try {
      if (!runIdRef.current) {
        const { data } = await tabletopExercisesAPI.list(projectId);
        const match = data.exercises.find(e => e.sessionId === sessionId && e.currentRunId);
        if (!match?.currentRunId) return; // not a tabletop session, or run hasn't STARTEXed yet
        runIdRef.current = match.currentRunId;
      }
      const { data } = await tabletopSecondScreenAPI.getState(projectId, runIdRef.current);
      setState(data.state);
    } catch (error: any) {
      if (STOP_STATUSES.has(error?.response?.status)) stop();
      // Other failures are ignored, matching useCinematicFeed.
    }
  }, [projectId, sessionId, stop]);

  useEffect(() => {
    stoppedRef.current = false;
    runIdRef.current = null;
    if (!isActive || !projectId || !tabletopAvailable) return;
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [isActive, projectId, tabletopAvailable, poll]);

  return state;
}
